import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  HistoricalPeriodItem,
  LocalFoodItem,
  LocationTopic,
  NamedImageItem,
  Topic,
  TopicStore,
  UnderstandingPoint,
  WikiTopic,
} from './types/detailsCard.types';
import {
  fetchGeocodingNominatim,
  fetchTopicWikidataFromNominatim,
} from '../../services/geocodingService';
import {
  makeCacheKey,
  getCachedSights,
  setCachedSights,
  streamTopSightsCSV,
  localizeNamedImageItems,
} from './services/topSightsEnrichment';
import { loadPrecomputedTopSightsByWikidataTag } from './services/precomputedTopSightsService';
import { fetchHistoricalPeriodsMarkdown } from './services/historicalPeriodsService';
import { streamRegionalDishes } from './services/localFoodService';
import { streamUnderstandingConcepts } from './services/understandingConceptsService';
import { resolveWikipediaUrlForTopic } from './services/wikipediaService';
import { fetchWikipediaArticleImages } from './services/wikiImagesService';

const topicPopulationCache = new Map<string, number | null>();
const topicPopulationInFlight = new Map<string, Promise<number | null>>();
const understandingInFlight = new Map<string, AbortController>();
const conciseOverviewInFlight = new Map<string, AbortController>();

const trimOverviewText = (input: string, maxChars = 140): string => {
  const text = String(input || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const sentenceParts = text.match(/[^.!?]+[.!?]+/g) || [];
  const oneSentence = sentenceParts.slice(0, 1).join(' ').trim();
  const candidate = oneSentence || text;

  if (candidate.length <= maxChars) return candidate;
  return `${candidate.slice(0, maxChars).trimEnd()}…`;
};

const getVeryShortOverviewFromSummaryPayload = (payload: any): string => {
  const shortDescription = trimOverviewText(String(payload?.description || ''), 110);
  if (shortDescription) return shortDescription;

  const extract = trimOverviewText(String(payload?.extract || ''), 140);
  return extract;
};

const getArticleTitleCandidates = (topic: WikiTopic): string[] => {
  const candidates = new Set<string>();
  const directName = String(topic?.name || '').trim();
  if (directName) candidates.add(directName);

  const link = String(topic?.wikiLink || '').trim();
  if (link && link.includes('/wiki/')) {
    try {
      const parsed = new URL(link);
      const fromPath = decodeURIComponent(parsed.pathname.split('/wiki/')[1] || '')
        .replace(/_/g, ' ')
        .trim();
      if (fromPath) candidates.add(fromPath);
    } catch {}
  }

  return Array.from(candidates);
};

const applyUnderstandingUpdate = (
  items: UnderstandingPoint[],
  update: { index: number; title?: string; body?: string; bodyDelta?: string },
): UnderstandingPoint[] => {
  if (!Number.isFinite(update.index) || update.index < 0) return items;
  const next = items.slice();
  while (next.length <= update.index) {
    next.push({ title: '', body: '' });
  }
  const current = next[update.index] || { title: '', body: '' };
  const nextTitle = update.title != null ? String(update.title) : current.title;
  const nextBody = update.bodyDelta != null
    ? `${current.body || ''}${String(update.bodyDelta)}`
    : (update.body != null ? String(update.body) : current.body);
  next[update.index] = { title: nextTitle, body: nextBody };
  return next;
};

const toUnderstandingPointKey = (point: UnderstandingPoint): string => {
  const title = String(point?.title || '').trim().toLowerCase();
  const body = String(point?.body || '').trim().toLowerCase();
  return `${title}\n${body}`;
};

const dedupeUnderstandingPoints = (items: UnderstandingPoint[]): UnderstandingPoint[] => {
  const seen = new Set<string>();
  const out: UnderstandingPoint[] = [];
  for (const point of items || []) {
    const title = String(point?.title || '').trim();
    const body = String(point?.body || '').trim();
    if (!title || !body) continue;
    const key = toUnderstandingPointKey({ title, body });
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ title, body });
  }
  return out;
};

const resolveTopicName = (geocodingData: any, lat: number, lng: number): string => {
  return (
    geocodingData?.topic_place_name ||
    geocodingData?.address?.city ||
    geocodingData?.address?.town ||
    geocodingData?.address?.village ||
    geocodingData?.address?.county ||
    geocodingData?.address?.state ||
    geocodingData?.address?.country ||
    geocodingData?.display_name ||
    `${lat.toFixed(4)},${lng.toFixed(4)}`
  );
};

const toTopicId = (topicName: string): string => {
  const normalized = String(topicName || '').trim().toLowerCase().replace(/\s+/g, ' ');
  return `location:name:${normalized || 'unknown'}`;
};

const toArticleTopicId = (article: any): string => {
  if (article?.pageid) return `article:${article.pageid}`;
  return `article:title:${String(article?.title || '').trim().toLowerCase().replace(/\s+/g, ' ')}`;
};

const toSightKey = (item: NamedImageItem): string => {
  return String(item?.id || item?.link || `${item?.label || ''}|${item?.imageUrl || ''}`)
    .trim()
    .toLowerCase();
};

const dedupeSights = (items: NamedImageItem[]): NamedImageItem[] => {
  const seen = new Set<string>();
  const out: NamedImageItem[] = [];
  for (const item of items || []) {
    const key = toSightKey(item);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const api = {
  // Topic Wikidata flow: second Nominatim reverse with extratags.
  getTopicResolution: async ({
    lat,
    lng,
    topicName,
    topicAdminLevel,
    userLanguage,
  }: {
    lat: number;
    lng: number;
    topicName: string;
    topicAdminLevel: string;
    userLanguage?: string;
  }): Promise<any> => {
    return fetchTopicWikidataFromNominatim({
      lat,
      lng,
      placeName: topicName,
      topicAdminLevel,
      userLanguage,
    });
  },

  getPopulation: async (wikidataTag: string): Promise<number | null> => {
    const cleanQid = String(wikidataTag || '').trim();
    if (!cleanQid) return null;

    if (topicPopulationCache.has(cleanQid)) {
      return topicPopulationCache.get(cleanQid) ?? null;
    }

    if (topicPopulationInFlight.has(cleanQid)) {
      return topicPopulationInFlight.get(cleanQid) ?? null;
    }

    const requestPromise = (async () => {
      const params = new URLSearchParams({
        action: 'wbgetentities',
        format: 'json',
        ids: cleanQid,
        props: 'claims',
        origin: '*',
      });

      const url = `https://www.wikidata.org/w/api.php?${params.toString()}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Wikidata population fetch failed: ${response.status}`);
      }

      const data = await response.json();
      const claims = data?.entities?.[cleanQid]?.claims?.P1082 || [];
      const values = claims
        .map((claim: any) => claim?.mainsnak?.datavalue?.value?.amount)
        .map((amount: any) => Number(String(amount ?? '').replace('+', '')))
        .filter((num: number) => Number.isFinite(num) && num > 0);

      const population = values.length ? Math.max(...values) : null;
      topicPopulationCache.set(cleanQid, population);
      return population;
    })();

    topicPopulationInFlight.set(cleanQid, requestPromise);
    try {
      return await requestPromise;
    } finally {
      topicPopulationInFlight.delete(cleanQid);
    }
  },
};

export const useTopicStore = create<TopicStore>()(
  immer((set, get) => ({
    topics: {},
    suggestedTopicId: null,
    activeTopicId: null,

    setActiveTopic: (topicId) => {
      set((state) => {
        state.activeTopicId = topicId;
      });
    },

    setTopic: (topic: Topic) => {
      set((state) => {
        const existing = state.topics[topic.id] as Topic | undefined;
        state.topics[topic.id] = existing ? ({ ...existing, ...topic } as Topic) : topic;
        state.activeTopicId = topic.id;
      });
    },

    setWikiArticleTopic: (article: any, params) => {
      const topicId = toArticleTopicId(article);
      const normalizedArticleSize = Number(article?.articleSizeBytes ?? article?.length ?? NaN);
      const existing = get().topics[topicId] as WikiTopic | undefined;
      const wikiTopic: WikiTopic = {
        id: topicId,
        type: 'wiki',
        name: article?.title || '',
        pageId: String(article?.pageid ?? ''),
        sourceWikiLang: article?.sourceWikiLang,
        thumbnail: article?.thumbnail,
        description: article?.originalDescription,
        wikiLink: article?.fullurl,
        articleSizeBytes: Number.isFinite(normalizedArticleSize) && normalizedArticleSize > 0
          ? normalizedArticleSize
          : null,
        conciseOverview: existing?.conciseOverview || '',
        conciseOverviewLoading: false,
        conciseOverviewError: null,
      };
      get().setTopic(wikiTopic);

      const viewportWikiLanguage = params?.viewportWikiLanguage || article?.sourceWikiLang || 'en';
      const userPreferredWikiLangCode = params?.userPreferredWikiLangCode || viewportWikiLanguage;
      void get().fetchWikiImages(topicId, { viewportWikiLanguage });
      void get().fetchConciseOverview(topicId, {
        viewportWikiLanguage,
        userPreferredWikiLangCode,
      });
    },

    activateSuggestedTopic: ({ userLanguageLabel, viewportWikiLanguage, userPreferredWikiLangCode }) => {
      const { suggestedTopicId } = get();
      if (!suggestedTopicId) return;
      set((state) => {
        state.activeTopicId = suggestedTopicId;
      });
      // Fetch top sights now that the user has explicitly activated this topic
      get().fetchTopSights(suggestedTopicId, {
        userLanguageLabel,
        viewportWikiLanguage,
        userPreferredWikiLangCode,
      });
    },

    handleNominatimResult: async (nominatimData, { userLanguageLabel, viewportWikiLanguage }) => {
      if (!nominatimData) return;

      const lat = parseFloat(nominatimData.lat);
      const lng = parseFloat(nominatimData.lon);
      const topicName = resolveTopicName(nominatimData, lat, lng);
      const hasCityLikeAddressField =
        !!nominatimData?.address?.city ||
        !!nominatimData?.address?.town ||
        !!nominatimData?.address?.village;

      let topicAdminLevel = String(
        nominatimData.topic_admin_level || nominatimData.addresstype || '',
      );

      // Guard against county-level selection when a city/town/village is present
      // (e.g. New York -> New York County instead of New York City).
      if (topicAdminLevel === 'county' && hasCityLikeAddressField) {
        if (nominatimData?.address?.city) topicAdminLevel = 'city';
        else if (nominatimData?.address?.town) topicAdminLevel = 'town';
        else topicAdminLevel = 'village';
      }

      const topicId = toTopicId(topicName);

      // Scaffold topic
      set((state) => {
        state.suggestedTopicId = topicId;
        if (!state.topics[topicId]) {
          state.topics[topicId] = {
            id: topicId,
            type: 'location',
            name: topicName,
            lat,
            lng,
          } as LocationTopic;
        }
      });

      // Enrich with Wikidata tag + population.
      // Always attempt a fresh resolution first so stale county/borough matches can self-correct.
      const existingTopic = get().topics[topicId] as LocationTopic;

      try {
        const topicResolution = await api.getTopicResolution({
          lat,
          lng,
          topicName,
          topicAdminLevel,
          userLanguage: viewportWikiLanguage,
        });
        const resolvedWikidataTag = typeof topicResolution === 'string'
          ? topicResolution
          : topicResolution?.qid || null;

        const wikidataTag =
          resolvedWikidataTag ||
          nominatimData.extratags?.wikidata ||
          existingTopic.wikidataTag ||
          null;

        if (!wikidataTag) return topicResolution;

        set((state) => {
          const topic = state.topics[topicId] as LocationTopic;
          if (topic) topic.wikidataTag = wikidataTag;
        });

        const population = await api.getPopulation(wikidataTag);
        if (Number.isFinite(population) && Number(population) > 0) {
          set((state) => {
            const topic = state.topics[topicId] as LocationTopic;
            if (topic) topic.population = Number(population);
          });
        }

        return topicResolution;
      } catch (error) {
        console.error('Enrichment failed', error);
        return null;
      }
    },

    handleMapCenterChange: async (lat, lng, { userLanguageLabel, viewportWikiLanguage }) => {
      // Standalone path: reverse-geocode first, then delegate to handleNominatimResult
      const geocodingData = await fetchGeocodingNominatim({
        lat,
        lng,
        zoom: 14,
        userLanguage: viewportWikiLanguage,
      });
      if (!geocodingData) return;
      await get().handleNominatimResult(geocodingData, { userLanguageLabel, viewportWikiLanguage });
    },

    fetchTopSights: async (topicId, {
      userLanguageLabel,
      viewportWikiLanguage,
      userPreferredWikiLangCode,
    }) => {
      const topic = get().topics[topicId] as LocationTopic | undefined;
      if (!topic || topic.type !== 'location') return;

      // Already loaded and not empty → skip
      if (
        topic.topSightsImages &&
        !topic.topSightsImages.isLoading &&
        topic.topSightsImages.items.length > 0
      ) {
        return;
      }

      const cacheKey = makeCacheKey(viewportWikiLanguage, userLanguageLabel, topic.name);

      // Check in-memory cache
      const cached = getCachedSights(cacheKey);
      if (cached && cached.length > 0) {
        const dedupedCached = dedupeSights(cached);
        console.log('[TopSights][PrecomputedPrefetch] not used (memory cache hit)', {
          topicId,
          topicName: topic.name,
          count: dedupedCached.length,
        });
        set((state) => {
          const t = state.topics[topicId] as LocationTopic;
          if (t) t.topSightsImages = { items: dedupedCached, isLoading: false };
        });
        return;
      }

      // Try precomputed Firestore cache by city Wikidata tag before live generation path
      const prefetchWikidataTag = String(topic.wikidataTag || '').trim();
      if (!prefetchWikidataTag) {
        console.log('[TopSights][PrecomputedPrefetch] not used (missing city wikidata tag)', {
          topicId,
          topicName: topic.name,
        });
      } else {
        console.log('[TopSights][PrecomputedPrefetch] lookup', {
          topicId,
          topicName: topic.name,
          wikidataTag: prefetchWikidataTag,
        });

        try {
          const precomputed = await loadPrecomputedTopSightsByWikidataTag({
            wikidataTag: prefetchWikidataTag,
          });

          if (precomputed && precomputed.length) {
            const dedupedPrecomputed = dedupeSights(precomputed);
            const localizationController = new AbortController();
            const localizedPrecomputed = dedupeSights(
              await localizeNamedImageItems({
                items: dedupedPrecomputed,
                viewportWikiLanguage,
                userPreferredWikiLangCode,
                signal: localizationController.signal,
              }),
            );

            console.log('[TopSights][PrecomputedPrefetch] used', {
              topicId,
              topicName: topic.name,
              wikidataTag: prefetchWikidataTag,
              count: localizedPrecomputed.length,
            });
            setCachedSights(cacheKey, localizedPrecomputed);
            set((state) => {
              const t = state.topics[topicId] as LocationTopic;
              if (t) t.topSightsImages = { items: localizedPrecomputed, isLoading: false };
            });
            return;
          }

          console.log('[TopSights][PrecomputedPrefetch] not used (no match for city wikidata tag)', {
            topicId,
            topicName: topic.name,
            wikidataTag: prefetchWikidataTag,
          });
        } catch (error) {
          console.warn('[TopSights][PrecomputedPrefetch] not used (lookup failed, falling back to live generation)', {
            topicId,
            topicName: topic.name,
            wikidataTag: prefetchWikidataTag,
            error,
          });
        }
      }

      // Set loading
      set((state) => {
        const t = state.topics[topicId] as LocationTopic;
        if (t) t.topSightsImages = { items: [], isLoading: true };
      });

      const controller = new AbortController();

      try {
        const allItems = await streamTopSightsCSV({
          topicName: topic.name,
          lat: topic.lat ?? 0,
          lng: topic.lng ?? 0,
          viewportWikiLanguage,
          userLanguageLabel,
          userPreferredWikiLangCode,
          signal: controller.signal,
          onRawItem: (item: NamedImageItem) => {
            // valid streamed row -> add skeleton placeholder immediately
            set((state) => {
              const t = state.topics[topicId] as LocationTopic;
              if (!t?.topSightsImages) return;

              const streamKey = String(item.streamKey || '').trim();
              if (!streamKey) return;

              const exists = t.topSightsImages.items.some(
                (existing) => String(existing.streamKey || '').trim() === streamKey,
              );
              if (exists) return;

              t.topSightsImages.items.push(item);
            });
          },
          onItem: (item: NamedImageItem) => {
            // enrichment completed -> replace placeholder with real image card
            set((state) => {
              const t = state.topics[topicId] as LocationTopic;
              if (!t?.topSightsImages) return;

              const incomingKey = toSightKey(item);
              const streamKey = String(item.streamKey || '').trim();

              if (streamKey) {
                const idx = t.topSightsImages.items.findIndex(
                  (existing) => String(existing.streamKey || '').trim() === streamKey,
                );
                if (idx >= 0) {
                  // If another card already resolved to the same canonical key,
                  // drop this placeholder instead of creating a duplicate.
                  const duplicateIdx = t.topSightsImages.items.findIndex(
                    (existing, existingIdx) => existingIdx !== idx && toSightKey(existing) === incomingKey,
                  );
                  if (duplicateIdx >= 0) {
                    t.topSightsImages.items.splice(idx, 1);
                    return;
                  }

                  t.topSightsImages.items[idx] = item;
                  return;
                }
              }

              const exists = t.topSightsImages.items.some((existing) => toSightKey(existing) === incomingKey);
              if (exists) return;

              t.topSightsImages.items.push(item);
            });
          },
        });

        // Cache result and mark done
        const dedupedItems = dedupeSights(allItems);
        if (dedupedItems.length > 0) {
          setCachedSights(cacheKey, dedupedItems);
        }
        set((state) => {
          const t = state.topics[topicId] as LocationTopic;
          if (t?.topSightsImages) {
            // Keep only successfully enriched items; drop unresolved placeholders.
            t.topSightsImages.items = dedupedItems;
            t.topSightsImages.isLoading = false;
          }
        });
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('[TopSights] fetchTopSights failed', error);
        }
        set((state) => {
          const t = state.topics[topicId] as LocationTopic;
          if (t?.topSightsImages) t.topSightsImages.isLoading = false;
        });
      }
    },

    fetchLocalFoods: async (topicId, { userLanguageLabel, viewportWikiLanguage }) => {
      const topic = get().topics[topicId] as LocationTopic | undefined;
      if (!topic || topic.type !== 'location') return;

      const current = topic.localFoods;
      if (current?.isLoading) return;
      if (
        current &&
        !current.isLoading &&
        current.items.length > 0 &&
        current.languageLabel === userLanguageLabel
      ) {
        return;
      }

      set((state) => {
        const t = state.topics[topicId] as LocationTopic | undefined;
        if (t) {
          t.localFoods = {
            items: [],
            isLoading: true,
            error: null,
            languageLabel: userLanguageLabel,
          };
        }
      });

      try {
        const seenTitles = new Set<string>();

        await streamRegionalDishes({
          locationName: topic.name,
          userLanguageLabel,
          viewportWikiLanguage,
          onItem: (item: LocalFoodItem) => {
            const titleKey = String(item.title || '').trim().toLowerCase();
            if (!titleKey || seenTitles.has(titleKey)) return;
            seenTitles.add(titleKey);

            set((state) => {
              const t = state.topics[topicId] as LocationTopic | undefined;
              if (!t?.localFoods) return;

              if (t.localFoods.items.some((d) => String(d.title || '').trim().toLowerCase() === titleKey)) {
                return;
              }

              t.localFoods.items.push(item);
            });
          },
        });

        set((state) => {
          const t = state.topics[topicId] as LocationTopic | undefined;
          if (t?.localFoods) {
            t.localFoods.isLoading = false;
            t.localFoods.error = null;
            t.localFoods.languageLabel = userLanguageLabel;
          }
        });
      } catch (error: any) {
        const message =
          error?.code === 'AUTH_REQUIRED' ? 'AUTH_REQUIRED' : 'could not connect to food service';

        set((state) => {
          const t = state.topics[topicId] as LocationTopic | undefined;
          if (t) {
            t.localFoods = {
              items: t.localFoods?.items ?? [],
              isLoading: false,
              error: message,
              languageLabel: userLanguageLabel,
            };
          }
        });
      }
    },

    fetchUnderstandingConcepts: async (topicId, { languageCode }) => {
      const topic = get().topics[topicId] as Topic | undefined;
      if (!topic) return;

      const current = topic.understandingConcepts;
      if (current?.isLoading || current?.isStreaming) return;
      if (
        current &&
        !current.isLoading &&
        !current.isStreaming &&
        current.items.length > 0 &&
        !current.error &&
        current.languageCode === languageCode
      ) {
        return;
      }

      const existingController = understandingInFlight.get(topicId);
      if (existingController) {
        existingController.abort();
      }
      const controller = new AbortController();
      understandingInFlight.set(topicId, controller);

      set((state) => {
        const t = state.topics[topicId] as Topic | undefined;
        if (!t) return;
        t.understandingConcepts = {
          items: [],
          isLoading: true,
          isStreaming: true,
          error: null,
          languageCode,
        };
      });

      try {
        const points = await streamUnderstandingConcepts({
          locationName: topic.name,
          languageCode,
          signal: controller.signal,
          onPoint: (update) => {
            set((state) => {
              const t = state.topics[topicId] as Topic | undefined;
              if (!t?.understandingConcepts) return;
              t.understandingConcepts.items = applyUnderstandingUpdate(t.understandingConcepts.items, update);
              t.understandingConcepts.isLoading = false;
              t.understandingConcepts.isStreaming = true;
              t.understandingConcepts.error = null;
            });
          },
        });

        set((state) => {
          const t = state.topics[topicId] as Topic | undefined;
          if (!t) return;
          t.understandingConcepts = {
            items: points,
            isLoading: false,
            isStreaming: false,
            error: null,
            languageCode,
          };
        });
      } catch (error: any) {
        if (error?.name === 'AbortError' || controller.signal.aborted) {
          return;
        }

        const message = String(error?.message || 'could not connect to understanding service');
        set((state) => {
          const t = state.topics[topicId] as Topic | undefined;
          if (!t) return;
          t.understandingConcepts = {
            items: t.understandingConcepts?.items ?? [],
            isLoading: false,
            isStreaming: false,
            error: message,
            languageCode,
          };
        });

        throw error;
      } finally {
        if (understandingInFlight.get(topicId) === controller) {
          understandingInFlight.delete(topicId);
        }
      }
    },

    fetchMoreUnderstandingConcepts: async (topicId, { languageCode }) => {
      const topic = get().topics[topicId] as Topic | undefined;
      if (!topic) return;

      const current = topic.understandingConcepts;
      if (current?.isLoading || current?.isStreaming) return;

      const priorPoints = dedupeUnderstandingPoints(current?.items ?? []);
      if (!priorPoints.length) {
        await get().fetchUnderstandingConcepts(topicId, { languageCode });
        return;
      }

      const existingController = understandingInFlight.get(topicId);
      if (existingController) {
        existingController.abort();
      }
      const controller = new AbortController();
      understandingInFlight.set(topicId, controller);

      const startIndex = priorPoints.length;

      set((state) => {
        const t = state.topics[topicId] as Topic | undefined;
        if (!t) return;
        t.understandingConcepts = {
          items: priorPoints,
          isLoading: false,
          isStreaming: true,
          error: null,
          languageCode,
        };
      });

      try {
        const points = await streamUnderstandingConcepts({
          locationName: topic.name,
          languageCode,
          priorPoints,
          signal: controller.signal,
          onPoint: (update) => {
            set((state) => {
              const t = state.topics[topicId] as Topic | undefined;
              if (!t?.understandingConcepts) return;
              t.understandingConcepts.items = applyUnderstandingUpdate(
                t.understandingConcepts.items,
                { ...update, index: startIndex + update.index },
              );
              t.understandingConcepts.isLoading = false;
              t.understandingConcepts.isStreaming = true;
              t.understandingConcepts.error = null;
            });
          },
        });

        set((state) => {
          const t = state.topics[topicId] as Topic | undefined;
          if (!t) return;
          t.understandingConcepts = {
            items: dedupeUnderstandingPoints([...priorPoints, ...points]),
            isLoading: false,
            isStreaming: false,
            error: null,
            languageCode,
          };
        });
      } catch (error: any) {
        if (error?.name === 'AbortError' || controller.signal.aborted) {
          return;
        }

        const message = String(error?.message || 'could not connect to understanding service');
        set((state) => {
          const t = state.topics[topicId] as Topic | undefined;
          if (!t) return;
          t.understandingConcepts = {
            items: dedupeUnderstandingPoints(t.understandingConcepts?.items ?? priorPoints),
            isLoading: false,
            isStreaming: false,
            error: message,
            languageCode,
          };
        });

        throw error;
      } finally {
        if (understandingInFlight.get(topicId) === controller) {
          understandingInFlight.delete(topicId);
        }
      }
    },

    fetchWikiImages: async (topicId, { viewportWikiLanguage }) => {
      const topic = get().topics[topicId] as WikiTopic | undefined;
      if (!topic || topic.type !== 'wiki') return;
      if (topic.pageId === 'prompt') return;

      if (topic.wikiImagesLoading) return;
      if (topic.wikiImages && topic.wikiImages.length > 0) return;
      if (topic.wikiImagesError) return;

      set((state) => {
        const t = state.topics[topicId] as WikiTopic | undefined;
        if (t) {
          t.wikiImagesLoading = true;
          t.wikiImagesError = null;
          t.wikiImages = [];
        }
      });

      const controller = new AbortController();

      try {
        const wikiLang = topic.sourceWikiLang || viewportWikiLanguage || 'en';
        const images = await fetchWikipediaArticleImages({
          title: topic.name,
          wikiLang,
          signal: controller.signal,
        });

        set((state) => {
          const t = state.topics[topicId] as WikiTopic | undefined;
          if (t) {
            t.wikiImages = images;
            t.wikiImagesLoading = false;
            t.wikiImagesError = null;
          }
        });
      } catch (error: any) {
        if (error?.name === 'AbortError') return;

        set((state) => {
          const t = state.topics[topicId] as WikiTopic | undefined;
          if (t) {
            t.wikiImages = [];
            t.wikiImagesLoading = false;
            t.wikiImagesError = 'could not connect to image service';
          }
        });
      }
    },

    fetchConciseOverview: async (topicId, { viewportWikiLanguage, userPreferredWikiLangCode }) => {
      const topic = get().topics[topicId] as WikiTopic | undefined;
      if (!topic || topic.type !== 'wiki') return;
      if (topic.pageId === 'prompt') return;

      const sourceLang = String(topic.sourceWikiLang || viewportWikiLanguage || 'en').trim() || 'en';
      const preferredLang = String(userPreferredWikiLangCode || viewportWikiLanguage || 'en').trim() || 'en';
      const requestKey = [
        topicId,
        preferredLang,
        sourceLang,
        String(topic.name || '').trim().toLowerCase(),
        String(topic.wikiLink || '').trim(),
      ].join('|');

      if (topic.conciseOverviewLoading && topic.conciseOverviewResolvedKey === requestKey) return;
      if (topic.conciseOverviewResolvedKey === requestKey && String(topic.conciseOverview || '').trim()) return;

      const existingController = conciseOverviewInFlight.get(topicId);
      if (existingController) {
        existingController.abort();
      }

      const fallback = trimOverviewText(String(topic.description || ''), 140);
      const controller = new AbortController();
      conciseOverviewInFlight.set(topicId, controller);

      set((state) => {
        const t = state.topics[topicId] as WikiTopic | undefined;
        if (!t || t.type !== 'wiki') return;
        t.conciseOverview = fallback;
        t.conciseOverviewLoading = true;
        t.conciseOverviewError = null;
        t.conciseOverviewResolvedKey = requestKey;
      });

      try {
        const titleCandidates = getArticleTitleCandidates(topic);
        const languageOrder = Array.from(new Set([preferredLang, 'en', sourceLang]));

        for (const lang of languageOrder) {
          for (const title of titleCandidates) {
            const url = `https://${lang}.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(
              title.replace(/\s+/g, '_'),
            )}`;
            const resp = await fetch(url, { signal: controller.signal });
            if (!resp.ok) continue;

            const payload = await resp.json().catch(() => ({} as any));
            const concise = getVeryShortOverviewFromSummaryPayload(payload);
            if (!concise) continue;

            set((state) => {
              const t = state.topics[topicId] as WikiTopic | undefined;
              if (!t || t.type !== 'wiki') return;
              t.conciseOverview = concise;
              t.conciseOverviewLoading = false;
              t.conciseOverviewError = null;
              t.conciseOverviewResolvedKey = requestKey;
            });
            return;
          }
        }

        set((state) => {
          const t = state.topics[topicId] as WikiTopic | undefined;
          if (!t || t.type !== 'wiki') return;
          t.conciseOverviewLoading = false;
          t.conciseOverviewError = null;
          t.conciseOverviewResolvedKey = requestKey;
        });
      } catch (error: any) {
        if (error?.name === 'AbortError') return;

        set((state) => {
          const t = state.topics[topicId] as WikiTopic | undefined;
          if (!t || t.type !== 'wiki') return;
          t.conciseOverviewLoading = false;
          t.conciseOverviewError = 'could not connect to summary service';
          t.conciseOverviewResolvedKey = requestKey;
        });
      } finally {
        if (conciseOverviewInFlight.get(topicId) === controller) {
          conciseOverviewInFlight.delete(topicId);
        }
      }
    },

    fetchWikipediaEmbed: async (topicId, { viewportWikiLanguage, userPreferredWikiLangCode }) => {
      const topic = get().topics[topicId] as Topic | undefined;
      if (!topic) return;

      const preferredLang = userPreferredWikiLangCode || 'en';
      const current = topic.wikipediaEmbed;
      if (current?.isLoading) return;
      if (
        current &&
        !current.isLoading &&
        !!current.url &&
        current.preferredLang === preferredLang
      ) {
        return;
      }

      set((state) => {
        const t = state.topics[topicId] as Topic | undefined;
        if (t) {
          t.wikipediaEmbed = {
            url: null,
            isLoading: true,
            error: null,
            preferredLang,
          };
        }
      });

      const controller = new AbortController();

      try {
        const resolved = await resolveWikipediaUrlForTopic({
          topic,
          viewportWikiLanguage,
          userPreferredWikiLangCode,
          signal: controller.signal,
        });

        set((state) => {
          const t = state.topics[topicId] as Topic | undefined;
          if (t) {
            t.wikipediaEmbed = {
              url: resolved.url,
              isLoading: false,
              error: resolved.error,
              preferredLang,
            };
          }
        });
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        set((state) => {
          const t = state.topics[topicId] as Topic | undefined;
          if (t) {
            t.wikipediaEmbed = {
              url: null,
              isLoading: false,
              error: 'could not connect to Wikipedia',
              preferredLang,
            };
          }
        });
      }
    },

    fetchHistoricalPeriods: async (topicId, { userLanguageLabel }) => {
      const topic = get().topics[topicId] as Topic | undefined;
      if (!topic) return;

      const current = topic.historicalPeriods;
      if (current?.isLoading) return;
      if (
        current &&
        !current.isLoading &&
        current.items.length > 0 &&
        current.languageLabel === userLanguageLabel
      ) {
        return;
      }

      set((state) => {
        const t = state.topics[topicId] as Topic | undefined;
        if (t) {
          t.historicalPeriods = {
            items: [],
            isLoading: true,
            error: null,
            languageLabel: userLanguageLabel,
          };
        }
      });

      const controller = new AbortController();

      try {
        const periods: HistoricalPeriodItem[] = await fetchHistoricalPeriodsMarkdown({
          topicName: topic.name,
          userLanguageLabel,
          signal: controller.signal,
        });

        set((state) => {
          const t = state.topics[topicId] as Topic | undefined;
          if (t) {
            t.historicalPeriods = {
              items: periods,
              isLoading: false,
              error: null,
              languageLabel: userLanguageLabel,
            };
          }
        });
      } catch (error: any) {
        if (error?.name === 'AbortError') return;

        const message =
          error?.code === 'AUTH_REQUIRED' ? 'AUTH_REQUIRED' : 'could not connect to history service';

        set((state) => {
          const t = state.topics[topicId] as Topic | undefined;
          if (t) {
            t.historicalPeriods = {
              items: [],
              isLoading: false,
              error: message,
              languageLabel: userLanguageLabel,
            };
          }
        });
      }
    },
  })),
);
